// Entscheidet, wie Select.vue im Guided-Layout des Unfallfragebogens
// dargestellt wird (Spec §3): Umschalter, Chips oder Dropdown.
// Reine Funktionen – ohne Vue, damit sie in Vitest (node) prüfbar sind.

export const SEGMENT_MIN_OPTIONS = 2;
export const SEGMENT_MAX_OPTIONS = 4;
export const SEGMENT_MAX_TITLE_LENGTH = 22;
export const CHIP_MAX_OPTIONS = 8;

export type ChoiceValue = string | number;
export type ChoiceVariant = "segments" | "chips" | "dropdown";

export interface ChoiceOption {
  value: ChoiceValue;
  title: string;
  disabled: boolean;
}

interface ToChoiceOptionsInput {
  items: readonly unknown[];
  valueKey: string | null;
  titleKey: string | null;
  /** Objekte nur aus den festen Katalogen (`type`) zulassen, nicht aus dem `items`-Prop. */
  allowObjects: boolean;
}

function isPrimitive(value: unknown): value is ChoiceValue {
  return typeof value === "string" || typeof value === "number";
}

/**
 * Vereinheitlicht die Einträge zu { value, title, disabled }.
 * Liefert null, wenn die Liste nicht eindeutig auflösbar ist (dann bleibt
 * es beim Dropdown): Objekte aus dem `items`-Prop sind dynamische Listen
 * (z. B. Personen), keine festen Optionen.
 */
export function toChoiceOptions({ items, valueKey, titleKey, allowObjects }: ToChoiceOptionsInput): ChoiceOption[] | null {
  const options: ChoiceOption[] = [];

  for (const item of items) {
    if (isPrimitive(item)) {
      options.push({ value: item, title: String(item), disabled: false });
      continue;
    }

    if (!allowObjects || item === null || typeof item !== "object") return null;

    const record = item as Record<string, unknown>;
    const title = titleKey ? record[titleKey] : undefined;
    const value = valueKey ? record[valueKey] : title;
    if (!isPrimitive(title) || !isPrimitive(value)) return null;

    options.push({ value, title: String(title), disabled: record.disabled === true });
  }

  return options;
}

interface ResolveChoiceVariantInput {
  guided: boolean;
  chips: boolean;
  options: readonly ChoiceOption[] | null;
}

export function resolveChoiceVariant({ guided, chips, options }: ResolveChoiceVariantInput): ChoiceVariant {
  if (!guided || !options || options.length === 0) return "dropdown";

  if (chips) return options.length <= CHIP_MAX_OPTIONS ? "chips" : "dropdown";

  const fitsCount = options.length >= SEGMENT_MIN_OPTIONS && options.length <= SEGMENT_MAX_OPTIONS;
  const fitsTitles = options.every((option) => option.title.length <= SEGMENT_MAX_TITLE_LENGTH);
  return fitsCount && fitsTitles ? "segments" : "dropdown";
}

/** Mehrfachauswahl: Wert hinzufügen bzw. entfernen – liefert immer ein neues Array. */
export function toggleChoiceValue(current: unknown, value: ChoiceValue): ChoiceValue[] {
  const list: ChoiceValue[] = Array.isArray(current) ? current : [];
  return list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];
}

/**
 * Pfeiltasten im Umschalter: nächste aktivierbare Option ab `fromIndex`
 * (mit Umlauf). `fromIndex = -1` → erste bzw. letzte. -1, wenn alles deaktiviert ist.
 */
export function nextEnabledIndex(disabledFlags: readonly boolean[], fromIndex: number, direction: 1 | -1): number {
  const count = disabledFlags.length;
  const start = fromIndex < 0 ? (direction === 1 ? -1 : count) : fromIndex;

  for (let step = 1; step <= count; step++) {
    const index = (((start + direction * step) % count) + count) % count;
    if (!disabledFlags[index]) return index;
  }
  return -1;
}
